import { PortalMessage } from '@components/portalMessage/PortalMessage';
import Loading from '@components/Loading';
import { useGetPolicy } from '@services/policy';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { QuoteGetResponse } from '@lio/api-types';
import { QuoteStatus } from '@models/quotes';
import { DownloadApplicationButton, yellowTextButtonStyles } from '@components/button/DownloadApplicationButton';
import { Box } from '@mui/material';
import { PortalMessageType } from '@components/portalMessage/PortalMessageType.enum';
import { Button } from '@components/button';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';

export const BoundQuotePage = ({ quote }: { quote: QuoteGetResponse }) => {
  const { data: policy, isLoading } = useGetPolicy({ policyId: quote?.policyId });
  const { isCustomerUser } = useStore((state) => state.userAuth, shallow);

  if (isLoading || !quote) {
    return <Loading />;
  }

  const failedToBindMultiPolicyQuote =
    quote?.multiPolicyQuoteDestinations?.some(
      (mp: { destinationQuote: { id: string; status?: { name: string } } | null }) =>
        mp.destinationQuote &&
        mp.destinationQuote.id !== quote?.id &&
        mp.destinationQuote?.status?.name !== QuoteStatus.Bound.name,
    ) ||
    (quote?.multiPolicyQuoteSource &&
      quote?.multiPolicyQuoteSource?.sourceQuote?.id !== quote?.id &&
      quote?.multiPolicyQuoteSource?.sourceQuote?.status?.name !== QuoteStatus.Bound.name);

  return (
    <PortalMessage
      type={PortalMessageType.Bound}
      title={["You're all set!", 'The policy is bound.']}
      subtitle={[
        <span key='document-text'>
          Policy documents for <b>{quote?.customer.name}</b> policy{' '}
          <b>
            {policy
              ? getFormattedPolicyNumber(policy.policyNumberPrefix, policy.policyNumber.policyNumber)
              : `${quote.policyNumber?.policyNumber}`}
          </b>{' '}
          have been generated and are now available.{' '}
          {failedToBindMultiPolicyQuote ? (
            <>
              However, a related quote has failed to bind. Please
              <Button
                color='secondary'
                href={`/quote/${quote.id}?viewReadOnly=true&sidebarItem=messages`}
                variant='text'
                sx={yellowTextButtonStyles}
              >
                message your Underwriter
              </Button>
              for assistance.
            </>
          ) : (
            !isCustomerUser && (
              <>
                You can
                <Button
                  color='secondary'
                  href={`/quote/${quote.id}?viewReadOnly=true&sidebarItem=messages`}
                  variant='text'
                  sx={yellowTextButtonStyles}
                >
                  message your Underwriter
                </Button>
                with any questions.
              </>
            )
          )}
        </span>,
        <Box
          key='thanks-text'
          component='span'
          sx={{ display: 'flex', flexDirection: 'column', fontWeight: isCustomerUser ? 'bold' : 'normal' }}
        >
          Thank you for choosing LIO!
          {!isCustomerUser && (
            <span>
              You may
              <DownloadApplicationButton
                label='download your application'
                quoteId={quote.id}
                buttonProps={{
                  color: 'secondary',
                  variant: 'text',
                  sx: yellowTextButtonStyles,
                }}
                quoteUpdatedDate={quote?.updatedAt}
              />
              for your records.
            </span>
          )}
        </Box>,
      ]}
      primaryBtnText='Go to Policy'
      primaryBtnAction={`/policy/${quote?.policyId}`}
      secondaryBtnText={isCustomerUser ? undefined : 'Start Another Quote'}
      secondaryBtnAction={isCustomerUser ? undefined : '/new-quote'}
      quote={quote}
    />
  );
};
